import { useRef, useState, useEffect } from 'react'
import { useRegisterUserMutation } from '../users/usersApiSlice'
import PulseLoader from 'react-spinners/PulseLoader'
import useTitle from '../../hooks/useTitle'
import { useTranslation } from 'react-i18next'
import AuthShell from '../../components/AuthShell'
import PasswordField from '../../components/PasswordField'
import GoogleSignIn from './GoogleSignIn'

const Register = () => {
    const { t } = useTranslation()
    useTitle(t('titles.register'))

    const userRef = useRef()
    const errRef = useRef()

    const [username, setUsername] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [errMsg, setErrMsg] = useState('')
    const [successMsg, setSuccessMsg] = useState('')

    const [registerUser, { isLoading }] = useRegisterUserMutation()

    useEffect(() => {
        userRef.current?.focus()
    }, [])

    useEffect(() => {
        setErrMsg('')
    }, [username, email, password, confirmPassword])

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (password !== confirmPassword) {
            setErrMsg(t('auth.errors.passwordsDoNotMatch'))
            errRef.current.focus()
            return
        }

        try {
            await registerUser({ username, email, password }).unwrap()
            setSuccessMsg(t('auth.registerSuccess', { username }))
            setUsername('')
            setEmail('')
            setPassword('')
            setConfirmPassword('')
        } catch (err) {
            if (!err.status) {
                setErrMsg(t('auth.errors.noServerResponse'))
            } else if (err.status === 409) {
                setErrMsg(err.data?.message || t('auth.errors.userExists'))
            } else if (err.status === 400) {
                setErrMsg(t('auth.errors.allFieldsRequired'))
            } else {
                setErrMsg(err.data?.message || t('auth.errors.registrationFailed'))
            }
            errRef.current.focus()
        }
    }

    const errClass = errMsg ? 'errmsg' : 'offscreen'

    if (isLoading) return <PulseLoader color={'#e8c56d'} />

    return (
        <AuthShell
            eyebrow={t('auth.eyebrow')}
            title={t('auth.createAccountHeading')}
            subtitle={successMsg ? undefined : t('auth.createAccountSubtitle')}
            links={[
                { to: '/login', label: t('auth.alreadyHaveAccount') },
                { to: '/', label: t('auth.backToHome') },
            ]}
        >
            <p ref={errRef} className={errClass} aria-live="assertive" tabIndex={-1}>{errMsg}</p>

            {successMsg ? (
                <div className="auth__body" aria-live="polite">
                    <p className="successmsg">{successMsg}</p>
                </div>
            ) : (
                <form className="form" onSubmit={handleSubmit}>
                    <div className="auth__field">
                        <label className="form__label" htmlFor="username">{t('auth.username')}</label>
                        <input
                            className="form__input"
                            type="text"
                            id="username"
                            ref={userRef}
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            autoComplete="username"
                            required
                        />
                    </div>

                    <div className="auth__field">
                        <label className="form__label" htmlFor="email">{t('auth.email')}</label>
                        <input
                            className="form__input"
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            required
                        />
                    </div>

                    <PasswordField
                        id="password"
                        label={t('auth.password')}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="new-password"
                    />

                    <PasswordField
                        id="confirmPassword"
                        label={t('auth.confirmPassword')}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                    />

                    <button className="form__submit-button">{t('auth.createAccount')}</button>
                </form>
            )}

            {!successMsg && (
                <GoogleSignIn onError={(msg) => { setErrMsg(msg); errRef.current?.focus() }} />
            )}
        </AuthShell>
    )
}

export default Register
